// utilities/workflowVariants.js
//
// PURE logic for AUTHORING workflow variants in /settings/workflows (SOUND-785,
// plan 2026-10-14 Phase 2): grouping templates into request type → variant →
// versions, the variant a new draft belongs to, and reading every refusal the
// API can return into something a screen shows in full. Pinned by
// workflowVariants.test.js (no component harness — SOUND-679).
//
// The create rule mirrors sound-api resolveVariantForCreate:
//   - a variantKey the type already uses → a NEW VERSION of that variant;
//   - a new variantKey → a NEW VARIANT, and variantLabel is required (except on
//     the type's very first template);
//   - variantKey omitted with 2+ variants → 400 VARIANT_KEY_REQUIRED.
// The API stays the enforcement point; these helpers only refuse earlier.

import { DEFAULT_VARIANT_KEY, variantKeyOf, variantDisplayLabel } from "utilities/ticketVariants";
import { suggestRequestTypeKey } from "utilities/requestTypes";

export const VARIANT_KEY_MAX = 64;
export const VARIANT_LABEL_MAX = 100;

/** lowercase snake_case: a letter, then a-z / 0-9 / _, max 64 — same as the API. */
export function isValidVariantKey(key) {
  return typeof key === "string" && /^[a-z][a-z0-9_]*$/.test(key) && key.length <= VARIANT_KEY_MAX;
}

/** "Net new (no Disputifier)" → "net_new_no_disputifier". */
export function suggestVariantKey(label) {
  return suggestRequestTypeKey(label);
}

/**
 * A template's place in its variant's history:
 *   "live"    — active (at most one per variant);
 *   "draft"   — never activated (activation bumps version from 1 to 2, so a row
 *               still at version 1 that is not live has never been live);
 *   "retired" — was live once and has been replaced or deactivated.
 */
export function templateStage(t) {
  if (t?.active === true) return "live";
  return (t?.version || 1) <= 1 ? "draft" : "retired";
}

const byUpdatedDesc = (a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);

/**
 * Group templates for the Workflow list.
 *
 * One row per request type that is ACTIVE or already has templates (a
 * deactivated type with workflows still needs to be reachable and labelled).
 * Each row lists its variants — default first, then other live ones, then the
 * rest by label — and each variant carries its live row, its drafts and how
 * many retired versions it has.
 *
 * @param {Object[]} requestTypes - admin rows `{key, label, active}` (includeInactive)
 * @param {Object[]} templates - every template (any state)
 * @returns {Array<{key, label, active, status, variants: Array, liveCount: number}>}
 */
export function groupWorkflows(requestTypes = [], templates = []) {
  const byType = new Map();
  for (const t of templates || []) {
    if (!t?.requestTypeKey) continue;
    if (!byType.has(t.requestTypeKey)) byType.set(t.requestTypeKey, []);
    byType.get(t.requestTypeKey).push(t);
  }

  const rtByKey = new Map((requestTypes || []).map((rt) => [rt.key, rt]));
  // Templates whose type the vocabulary does not list still show (raw key) —
  // hiding them would hide live workflows.
  const keys = new Set([
    ...(requestTypes || []).filter((rt) => rt.active !== false || byType.has(rt.key)).map((rt) => rt.key),
    ...byType.keys(),
  ]);

  return [...keys].map((key) => {
    const rt = rtByKey.get(key);
    const label = rt?.label || key;
    const rows = byType.get(key) || [];
    const vmap = new Map();
    for (const t of [...rows].sort(byUpdatedDesc)) {
      const vk = variantKeyOf(t);
      if (!vmap.has(vk)) vmap.set(vk, { variantKey: vk, live: null, drafts: [], retired: [], latest: t });
      const v = vmap.get(vk);
      const stage = templateStage(t);
      if (stage === "live") v.live = t;
      else if (stage === "draft") v.drafts.push(t);
      else v.retired.push(t);
    }
    const variants = [...vmap.values()].map((v) => {
      const labelSource = v.live || v.latest;
      return {
        variantKey: v.variantKey,
        variantLabel: variantDisplayLabel(labelSource, label),
        isDefault: !!v.live && v.live.isDefault === true,
        live: v.live,
        drafts: v.drafts,
        retiredCount: v.retired.length,
        latest: v.latest,
        // Where a click on the variant goes: the live row, else its newest row.
        primary: v.live || v.latest,
      };
    });
    variants.sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      if (!!a.live !== !!b.live) return a.live ? -1 : 1;
      return a.variantLabel.localeCompare(b.variantLabel);
    });
    const liveCount = variants.filter((v) => v.live).length;
    const status = liveCount > 0 ? "active" : rows.length > 0 ? "draft" : "none";
    return { key, label, active: rt ? rt.active !== false : true, status, variants, liveCount };
  });
}

/**
 * The distinct variants of ONE request type, for the editor's "new version of"
 * picker: `[{variantKey, variantLabel, live, isDefault, primary}]`.
 */
export function variantsForType(templates = [], requestTypeKey, requestTypeLabel) {
  const group = groupWorkflows(
    [{ key: requestTypeKey, label: requestTypeLabel || requestTypeKey, active: true }],
    (templates || []).filter((t) => t.requestTypeKey === requestTypeKey)
  ).find((g) => g.key === requestTypeKey);
  return group ? group.variants : [];
}

/**
 * The variant mode a NEW draft starts in:
 *   "first"    — the type has no templates; the draft becomes its first
 *                workflow (the API keys it "default");
 *   "existing" — a new version of a variant (the one named, else the default /
 *                the only one);
 *   "new"      — a new variant.
 * A request for "existing" with an unknown key falls back to "new" with that
 * key suggested, rather than silently drafting a version of the wrong variant.
 */
export function initialVariantMode(variants = [], { variantKey, newVariant = false } = {}) {
  if (!variants || variants.length === 0) return { mode: "first", variantKey: "" };
  if (newVariant) return { mode: "new", variantKey: "" };
  if (variantKey) {
    return variants.some((v) => v.variantKey === variantKey)
      ? { mode: "existing", variantKey }
      : { mode: "new", variantKey };
  }
  const def = variants.find((v) => v.isDefault) || variants[0];
  return { mode: "existing", variantKey: def.variantKey };
}

/**
 * Issues with the variant choice on a NEW draft, before the round trip.
 * @param {{mode: string, variantKey?: string, variantLabel?: string}} choice
 * @param {Object[]} variants - the type's existing variants
 * @returns {string[]}
 */
export function variantChoiceIssues(choice = {}, variants = []) {
  const out = [];
  const label = (choice.variantLabel || "").trim();
  if (label.length > VARIANT_LABEL_MAX) out.push(`Variant name must be at most ${VARIANT_LABEL_MAX} characters.`);
  if (choice.mode === "existing") {
    if (!choice.variantKey || !variants.some((v) => v.variantKey === choice.variantKey)) {
      out.push("Pick the variant this draft is a new version of.");
    }
  } else if (choice.mode === "new") {
    const key = choice.variantKey || "";
    if (!label) out.push("A new variant needs a name.");
    if (!key) out.push("A new variant needs a key.");
    else if (!isValidVariantKey(key))
      out.push("Variant key must be lowercase snake_case: starts with a letter, then a–z, 0–9 or _ (max 64).");
    else if (variants.some((v) => v.variantKey === key))
      out.push(`Variant key "${key}" is already used by this request type — pick "New version of" instead.`);
  }
  return out;
}

/**
 * The variant fields for POST /ticket-templates.
 *   first    → nothing but an optional label (the API keys it "default" and
 *              labels it with the request type when no label is given);
 *   existing → the key (and a label only if one was typed — the API keeps the
 *              variant's current label otherwise);
 *   new      → key + label.
 */
export function variantCreateFields(choice = {}) {
  const label = (choice.variantLabel || "").trim();
  if (choice.mode === "existing") {
    return label ? { variantKey: choice.variantKey, variantLabel: label } : { variantKey: choice.variantKey };
  }
  if (choice.mode === "new") return { variantKey: choice.variantKey, variantLabel: label };
  return label ? { variantLabel: label } : {};
}

/**
 * The variant field for PUT /ticket-templates/:id. Only the label is editable
 * (the key is locked, isDefault moves via make-default). A blank label is not
 * sent — the API refuses to clear one, and a legacy row with no label should
 * stay that way until someone names it.
 */
export function variantUpdateFields(variantLabel) {
  const label = (variantLabel || "").trim();
  return label ? { variantLabel: label } : {};
}

const CODE_TITLES = {
  VARIANT_KEY_REQUIRED: "Say which variant this is",
  INVALID_VARIANT_KEY: "Invalid variant key",
  INVALID_VARIANT_LABEL: "Invalid variant name",
  VARIANT_LABEL_REQUIRED: "A new variant needs a name",
  IS_DEFAULT_NOT_EDITABLE: "The default is not edited here",
  VARIANT_KEY_LOCKED: "The variant key is locked",
  TEMPLATE_NOT_ACTIVE: "Only a live variant can be the default",
  DEFAULT_VARIANT_REQUIRED: "This is the default variant",
  VARIANT_ACTIVATION_CONFLICT: "Changed at the same time",
  DEFAULT_VARIANT_CONFLICT: "Changed at the same time",
  NO_DEFAULT_VARIANT: "No default variant",
  INVALID_TEMPLATE_FOR_REQUEST_TYPE: "That workflow variant is no longer live",
  TEMPLATE_REQUIRES_REQUEST_TYPE: "Pick a request type",
  VARIANT_SELECTION_INTERNAL_ONLY: "Variant selection is internal-only",
};

function variantRefs(list) {
  return (Array.isArray(list) ? list : []).map((v) => ({
    templateId: v?.templateId ?? null,
    variantKey: v?.variantKey || DEFAULT_VARIANT_KEY,
    variantLabel: v?.variantLabel || null,
    isDefault: v?.isDefault === true,
  }));
}

/**
 * Read a workflow-template (or ticket-create) failure into a panel model.
 * NEVER swallows: an unrecognised body still yields its `error`, and a network
 * failure still yields its message.
 *
 * @returns {{title: string, message: string, code: string|null, status: number|null,
 *            details: string[], variants: Array<{templateId, variantKey, variantLabel, isDefault}>}}
 *   `variants` is `activeVariants` (DEFAULT_VARIANT_REQUIRED) or `variants`
 *   (VARIANT_KEY_REQUIRED) — the list the user needs to act on.
 */
export function describeTemplateError(err, fallbackTitle = "Request failed") {
  const status = err?.response?.status ?? null;
  const body = err?.response?.data || {};
  const code = typeof body.code === "string" ? body.code : null;
  const details = Array.isArray(body.details) ? body.details.map(String) : [];
  const message =
    body.error ||
    (details.length > 0 ? "Template validation failed" : null) ||
    err?.message ||
    "Unknown error";
  const title = (code && CODE_TITLES[code]) || (details.length > 0 ? "The server refused this template" : fallbackTitle);
  const variants = variantRefs(body.activeVariants || body.variants);
  return { title, message, code, status, details, variants };
}
